// src/components/channels/ChannelCard.tsx
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Channel, disconnectChannel, deleteChannel } from '../../services/channelService';
import { MessageSquareText, Unplug, Trash2, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

interface ChannelCardProps {
  channel: Channel;
}

export default function ChannelCard({ channel }: ChannelCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRescanQR = () => {
    // Aquí deberías abrir el modal de QR o disparar la acción para re-escanear
    window.dispatchEvent(new CustomEvent('openQrModal', { detail: { channelId: channel.id } }));
  };

  const handleDisconnect = async () => {
    if (!confirm(t('confirmDisconnectChannel', 'Are you sure you want to disconnect this channel? You can reconnect it later.'))) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const result = await disconnectChannel(channel.id) as unknown as { success: boolean; message: string };
      toast.success(result?.message || t('channelDisconnectedSuccessfully', 'Channel disconnected successfully'));
      // Refrescar la lista de canales
      await queryClient.invalidateQueries({ queryKey: ['channels'] });
    } catch (error) {
      console.error('Error disconnecting channel:', error);
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(errorMessage || t('errorDisconnectingChannel', 'Error disconnecting channel'));
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('confirmDeleteChannel', 'Are you sure you want to DELETE this channel? This action cannot be undone and will remove all associated data.'))) {
      return;
    }

    // Segunda confirmación para acción destructiva
    if (!confirm(t('confirmDeleteChannelFinal', 'FINAL WARNING: This will permanently delete the channel and all its data. Continue?'))) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteChannel(channel.id) as unknown as { success: boolean; message: string };
      toast.success(result?.message || t('channelDeletedSuccessfully', 'Channel deleted successfully'));
      // Refrescar la lista de canales
      await queryClient.invalidateQueries({ queryKey: ['channels'] });
    } catch (error) {
      console.error('Error deleting channel:', error);
      const errorMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(errorMessage || t('errorDeletingChannel', 'Error deleting channel'));
    } finally {
      setIsDeleting(false);
    }
  };
  return (
    <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:shadow-md">
      <div className="mb-3 flex items-center">
        <MessageSquareText className="mr-3 h-8 w-8 flex-shrink-0 text-indigo-600" />
        <h3 className="truncate text-lg font-semibold text-gray-900" title={channel.nameChannel || undefined}>
          {channel.nameChannel}
        </h3>
      </div>
      {channel.phoneChannel && (
        <p className="mb-1 text-xs text-gray-500 flex-grow min-h-[20px]">
          {channel.phoneChannel}
        </p>
      )}
      {/* Estado de conexión WhatsApp y suscripción */}
      <div className="mb-2">

        {/* {channel.isAuthenticated ? (
          <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 font-semibold">Autenticado</span>
        ) : (
          <>
            <span className="inline-block px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 font-semibold mr-2">No conectado</span>
            <button
              className="inline-block px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 font-semibold hover:bg-yellow-200 transition"
              onClick={handleRescanQR}
              title="Re-escanear QR para conectar WhatsApp"
            >
              Re-escanear QR
            </button>
          </>
        )} */}

        {channel.subscriptionStatus === 'inactive' ? (
          <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700 font-semibold mr-2">{t('noActiveSubscription')}</span>
        ) : channel.isAuthenticated && channel.isReconnecting ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 font-semibold">
            <Loader2 className="h-3 w-3 animate-spin" />
            {t('reconnecting')}
          </span>
        ) : channel.isAuthenticated ? (
          <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 font-semibold">{t('authenticated')}</span>
        ) : (
          <>
            <span className="inline-block px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 font-semibold mr-2">{t('notConnected')}</span>
            <button
              className="inline-block px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800 font-semibold hover:bg-yellow-200 transition"
              onClick={handleRescanQR}
              title={t('rescanQrToConnect')}
            >
              {t('rescanQr')}
            </button>
          </>
        )}
      </div>
      <div className="mt-auto pt-3 space-y-2">
        <Link 
          to={`/channel/${channel.id}`}
          className="w-full block text-center rounded-md bg-indigo-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          {t('openChannel')}
        </Link>
        
        {/* Acciones secundarias */}
        <div className="flex gap-2">
          {/* Botón desconectar - solo visible si está autenticado */}
          {channel.isAuthenticated && (
            <button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="flex-1 flex items-center justify-center gap-1 rounded-md bg-orange-500 px-2 py-1.5 text-xs font-medium text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('disconnectChannel', 'Disconnect channel')}
            >
              <Unplug className="h-3 w-3" />
              {isDisconnecting ? t('disconnecting', 'Disconnecting...') : t('disconnect', 'Disconnect')}
            </button>
          )}
          
          {/* Botón eliminar - siempre visible */}
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className={`${channel.isAuthenticated ? 'flex-1' : 'w-full'} flex items-center justify-center gap-1 rounded-md bg-red-500 px-2 py-1.5 text-xs font-medium text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
            title={t('deleteChannel', 'Delete channel')}
          >
            <Trash2 className="h-3 w-3" />
            {isDeleting ? t('deleting', 'Deleting...') : t('delete', 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
